import { useRef, useState } from 'react'
import noop from 'lodash/noop'
import { unstable_batchedUpdates as batch } from 'react-dom'
import fetch from '~/common/fetch'
import { AbortError } from '~/common/fetchErrors'
import jsonata from '~/common/jsonata'
import useDebouncedEffect from '~/hooks/useDebouncedEffect'
import usePrevious from '~/hooks/usePrevious'
import { useStateRef } from '~/hooks/useStateRef'
import { useLatestCallbackRef } from '~/hooks/useLatestRef'

export const DEFAULT_URL_CHANGE_FETCH_DEBOUNCE_DELAY = 50

/**
 * A hook that will fetch a resource whenever the url changes and optionally transform and debounce.
 *
 * @param {String} url Whenever the url changes, a fetch will be made.
 * @param {boolean} enabled Whether the fetching is enabled or not
 * @param {String} [transform] A jsonata transformation to apply to the fetch response
 * @param {String} [transformContext] A dictionary to pass to the transform for context resolving
 * @param {Number} [delay=50] A debounce delay for url changes before fetching
 * @param {Boolean} [initial=false] Whether to call the fetch on initial mount
 * @param {Object} [fetchOptions] `fetch` options
 * @param {Function} [sideEffect=noop] An optional callback to perform side-effects with the fetch response
 * @param fetchFn
 * @param debugFn
 * @param debugName
 * @returns {[response: any, info: { loading: boolean, loadCountRef: import('react').RefObject<number>, resetLoadCount: () => void }]}
 */
export function useFetchOnUrlChange({
  url,
  enabled = true,
  transform,
  delay,
  fetchOptions,
  initial,
  sideEffect = noop,
  fetchFn,
  debugFn = noop,
  debugName,
  transformContext
}) {
  const [response, setResponse] = useState([])
  const [fetchingRef, setFetching] = useStateRef(false)
  const prevUrl = usePrevious(url)
  const urlHasChanged = url !== prevUrl && prevUrl !== undefined
  const loading = urlHasChanged || fetchingRef.current
  const [loadCountRef, setLoadCount] = useStateRef(0)

  const debug = debugFn?.useOnArgsChange ?? debugFn
  debug?.(`useFetchOnUrlChange [${debugName}]`, {
    url,
    urlHasChanged,
    fetching: fetchingRef.current,
    loading,
    enabled,
    transform,
    delay,
    fetchOptions,
    initial,
    transformContext
  })

  /**
   * @type {React.MutableRefObject<undefined | AbortController>}
   */
  const abortControllerRef = useRef(undefined)

  useDebouncedEffect(
    ({ initial, leading }) => {
      function abortFetch() {
        if (fetchingRef.current && abortControllerRef.current) {
          abortControllerRef.current?.abort?.(new AbortError())
          debugFn(`fetch ABORT [${debugName}]`, url, abortControllerRef.current.signal.aborted)
        }
      }

      // Sometimes there is an edge case where the fetch has been started but then the enabled is set to false
      // In those case we want to abort and reset back to a non-loading state
      if (!enabled && fetchingRef.current) {
        abortFetch()
        setFetching(false)
      }

      if (!url || !enabled) return

      if (initial || leading) {
        debugFn(`fetch PRESTART [${debugName}]`, { url, leading })
        setFetching(true)
      }

      if (initial || !leading) {
        // Before we kick off an API call, let's abort any that might be in flight.
        abortFetch()
        abortControllerRef.current = new AbortController()

        // Make the API call
        debugFn(`fetch START [${debugName}]`, url)
        ;(fetchFn ?? defaultFetchWithTransform)({
          url,
          transform,
          fetchOptions: { ...fetchOptions, signal: abortControllerRef.current.signal },
          transformContext,
          debugName
        }).then(function success(response) {
          debugFn(`fetch END [${debugName}]`, url, response)
          batch(() => {
            setResponse(response)
            setFetching(false, { strategy: 'next_render' })
            setLoadCount(prev => prev + 1)
            sideEffect(response)
          })
        })
      }
    },
    {
      delay: delay ?? DEFAULT_URL_CHANGE_FETCH_DEBOUNCE_DELAY,
      leading: true,
      initial
    },
    [url, enabled]
  )

  const resetLoadCount = useLatestCallbackRef(() => {
    setLoadCount(0)
  })

  const reset = useLatestCallbackRef(() => {
    debugFn(`fetch RESET [${debugName}]`, url)
    setResponse([])
    setLoadCount(0)
    setFetching(false, { strategy: 'next_render' })
  })

  return [response, { loading, loadCountRef, resetLoadCount, reset }]
}

async function defaultFetchWithTransform({ url, transform, fetchOptions, transformContext }) {
  const raw = await fetch.get(url, fetchOptions)
  return transform ? jsonata(transform).evaluate(raw, transformContext) : raw
}
